import * as React from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import {
  fetchBrokerCapabilities,
  fetchCtraderOAuthAuthorizeUrl,
  getBrokerCredentials,
  saveBrokerCredentials,
  testBrokerConnection,
} from "@/services/brokerIntegrationApi";
import type { ResolveInstrumentResponse } from "@/types/instruments";
import type { InstrumentSetupRow } from "@/types/instrumentSetup";
import type { MarketDataSourceOption } from "@/types/marketDataSources";
import {
  stashCtraderOAuthAssetClasses,
  stashCtraderOAuthEnvironment,
  stashCtraderOAuthMdFetchConsent,
  clearCtraderOAuthPending,
  resolveCtraderOpenApiSync,
  type CtraderOpenApiSyncInfo,
} from "@/utils/ctraderOpenApiSync";
import {
  isCtraderOAuthPopupMessage,
  openCtraderOAuthPopup,
} from "@/utils/ctraderOAuthPopup";
import {
  buildInlineLiquidBrokerCredentialPayload,
  isInlineCtraderMarketDataReconnect,
  isInlineLiquidMarketDataReconnect,
  resolveMarketDataBrokerReconnectConsentLabel,
  saveInstrumentMarketDataPreferenceAfterReconnect,
} from "@/utils/marketDataBrokerReconnect";
import { humanizeMarketDataBrokerSlug } from "@/utils/marketDataSourceChoice";
import type { InstrumentRowEditorSelection } from "@/utils/instrumentSetupSave";
import { presentMutationError, toastError } from "@/utils/showApiErrorToast";

export type MarketDataBrokerReconnectDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  broker: string;
  symbol: string;
  brokerAccountCode: string;
  row: InstrumentSetupRow;
  source: MarketDataSourceOption;
  selection: InstrumentRowEditorSelection;
  resolveContext?: Pick<
    ResolveInstrumentResponse,
    "md_listing_key" | "instrument_id" | "asset_class"
  >;
  onFlowComplete: () => Promise<void>;
};

function brokerLabel(broker: string, source: MarketDataSourceOption): string {
  return (
    source.broker_display?.trim() ||
    source.market_data_broker_display?.trim() ||
    humanizeMarketDataBrokerSlug(broker) ||
    "Broker"
  );
}

type SharedFlowProps = {
  open: boolean;
  symbol: string;
  display: string;
  brokerAccountCode: string;
  row: InstrumentSetupRow;
  source: MarketDataSourceOption;
  brokerSlug: string;
  selection: InstrumentRowEditorSelection;
  resolveContext?: MarketDataBrokerReconnectDialogProps["resolveContext"];
  onFlowComplete: () => Promise<void>;
  onOpenChange: (open: boolean) => void;
  onBusyChange: (busy: boolean) => void;
};

function useSavePreferenceAfterReconnect(props: SharedFlowProps) {
  return React.useCallback(async () => {
    await saveInstrumentMarketDataPreferenceAfterReconnect({
      brokerAccountCode: props.brokerAccountCode,
      symbol: props.symbol,
      row: props.row,
      selection: props.selection,
      resolveContext: props.resolveContext,
    });
    await props.onFlowComplete();
  }, [props]);
}

function ConsentField(props: {
  id: string;
  symbol: string;
  source: MarketDataSourceOption;
  brokerSlug: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}): React.ReactElement {
  return (
    <label
      htmlFor={props.id}
      className={cn(
        BROKER_ALERT_DESC_CLASS,
        "flex cursor-pointer items-start gap-3 rounded-lg border border-[color-mix(in_oklab,var(--brand-tropical-surf)_45%,transparent)] bg-[color-mix(in_oklab,var(--brand-tropical-surf)_10%,transparent)] p-3"
      )}
    >
      <Checkbox
        id={props.id}
        checked={props.checked}
        disabled={props.disabled}
        onCheckedChange={(value) => props.onCheckedChange(value === true)}
        className="mt-0.5 shrink-0"
      />
      <span className="min-w-0 leading-5">
        {resolveMarketDataBrokerReconnectConsentLabel({
          symbol: props.symbol,
          source: props.source,
          broker: props.brokerSlug,
        })}
      </span>
    </label>
  );
}

function LiquidMarketDataReconnectForm(props: SharedFlowProps): React.ReactElement {
  const [loading, setLoading] = React.useState(false);
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [consentGranted, setConsentGranted] = React.useState(false);
  const [testPassed, setTestPassed] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [testMessage, setTestMessage] = React.useState<string | null>(null);

  const savePreference = useSavePreferenceAfterReconnect(props);
  const { brokerSlug, display, symbol, onOpenChange } = props;

  React.useEffect(() => {
    if (!props.open) return;
    let cancelled = false;
    setLoading(true);
    setPassword("");
    setConsentGranted(false);
    setTestPassed(false);
    setTestMessage(null);
    void (async () => {
      try {
        const saved = await getBrokerCredentials(brokerSlug);
        if (cancelled) return;
        setUsername(String(saved?.username ?? saved?.Username ?? "").trim());
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brokerSlug, props.open]);

  React.useEffect(() => {
    props.onBusyChange(loading || testing || submitting);
  }, [loading, props, submitting, testing]);

  const invalidateTest = React.useCallback(() => {
    setTestPassed(false);
    setTestMessage(null);
  }, []);

  const buildPayload = React.useCallback(
    (mdFetchConsent: boolean) =>
      buildInlineLiquidBrokerCredentialPayload({
        username,
        password,
        domain: "default",
        mdFetchConsent,
      }),
    [password, username]
  );

  const handleTest = async () => {
    if (!username.trim() || !password.trim()) {
      toastError("Enter your Liquid Charts username and password.");
      return;
    }
    setTesting(true);
    invalidateTest();
    try {
      const result = await testBrokerConnection(brokerSlug, buildPayload(false));
      if (!result.success) {
        setTestMessage(result.message);
        toastError("Connection test failed", { description: result.message });
        return;
      }
      setTestPassed(true);
      setTestMessage(result.message);
      toast.success("Connection verified");
    } catch (error) {
      presentMutationError(error, "Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleConnectAndConfirm = async () => {
    if (!consentGranted) {
      toastError("Check the consent box to continue.");
      return;
    }
    if (!testPassed) {
      toastError("Run Test connection successfully before continuing.");
      return;
    }
    setSubmitting(true);
    try {
      await saveBrokerCredentials(brokerSlug, buildPayload(true));
      await savePreference();
      toast.success(`Liquid Charts connected for ${symbol}`);
      onOpenChange(false);
    } catch (error) {
      presentMutationError(error, `Could not connect ${display}`);
    } finally {
      setSubmitting(false);
    }
  };

  const busy = loading || testing || submitting;

  return (
    <>
      <div className="flex flex-col gap-4">
        <ol className={cn(BROKER_FIELD_DESC_CLASS, "m-0 list-decimal space-y-1 pl-5 text-sm")}>
          <li>Verify your {display} login</li>
          <li>Confirm OHLC for {symbol} on this account</li>
        </ol>

        {loading ? (
          <div className={cn(BROKER_FIELD_DESC_CLASS, "flex items-center gap-2")}>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Loading saved login…
          </div>
        ) : (
          <>
            <Field className="gap-2">
              <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Username</FieldLabel>
              <Input
                id="md-reconnect-username"
                autoComplete="username"
                value={username}
                disabled={busy}
                onChange={(event) => {
                  setUsername(event.target.value);
                  invalidateTest();
                }}
                placeholder="Liquid Charts username or email"
              />
            </Field>
            <Field className="gap-2">
              <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Password</FieldLabel>
              <Input
                id="md-reconnect-password"
                type="password"
                autoComplete="current-password"
                value={password}
                disabled={busy}
                onChange={(event) => {
                  setPassword(event.target.value);
                  invalidateTest();
                }}
                placeholder="Liquid Charts password"
              />
              <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                Used only to restore your broker connection for chart OHLC — no trade sync.
              </FieldDescription>
            </Field>

            <Button
              type="button"
              variant="outline-brand"
              size="sm"
              data-font="primary"
              className="h-8 w-fit px-5 text-[0.75rem] normal-case tracking-normal"
              disabled={busy || !username.trim() || !password.trim()}
              onClick={() => void handleTest()}
            >
              {testing ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Testing…
                </>
              ) : (
                "Test connection"
              )}
            </Button>

            {testMessage ? (
              <p
                className={cn(
                  BROKER_FIELD_DESC_CLASS,
                  "m-0 text-sm",
                  testPassed ? "text-emerald-600 dark:text-emerald-400" : undefined
                )}
              >
                {testMessage}
              </p>
            ) : null}

            <ConsentField
              id="md-reconnect-consent-liquid"
              symbol={symbol}
              source={props.source}
              brokerSlug={brokerSlug}
              checked={consentGranted}
              disabled={busy}
              onCheckedChange={setConsentGranted}
            />
          </>
        )}
      </div>

      <div className="mt-4 flex w-full flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
        <Button
          type="button"
          variant="outline-gray"
          size="sm"
          data-font="primary"
          className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
          disabled={busy}
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
          disabled={busy || !consentGranted || !testPassed}
          onClick={() => void handleConnectAndConfirm()}
        >
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Saving…
            </>
          ) : (
            `Connect & confirm ${symbol}`
          )}
        </Button>
      </div>
    </>
  );
}

function CtraderMarketDataReconnectForm(props: SharedFlowProps): React.ReactElement {
  const [loading, setLoading] = React.useState(true);
  const [openApiSync, setOpenApiSync] = React.useState<CtraderOpenApiSyncInfo | null>(null);
  const [environment, setEnvironment] = React.useState<"demo" | "live">("demo");
  const [consentGranted, setConsentGranted] = React.useState(false);
  const [oauthStarting, setOauthStarting] = React.useState(false);
  const [oauthWaiting, setOauthWaiting] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const popupPollRef = React.useRef<number | null>(null);

  const savePreference = useSavePreferenceAfterReconnect(props);
  const { symbol, onOpenChange, selection } = props;

  const clearPopupWatch = React.useCallback(() => {
    if (popupPollRef.current != null) {
      window.clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
  }, []);

  const finishAfterOAuth = React.useCallback(async () => {
    setSubmitting(true);
    try {
      await savePreference();
      toast.success(`cTrader connected for ${symbol}`);
      onOpenChange(false);
    } catch (error) {
      presentMutationError(error, `Could not confirm OHLC for ${symbol}`);
    } finally {
      setSubmitting(false);
      setOauthWaiting(false);
    }
  }, [onOpenChange, savePreference, symbol]);

  React.useEffect(() => {
    if (!props.open) return;
    let cancelled = false;
    setLoading(true);
    setConsentGranted(false);
    setOauthStarting(false);
    setOauthWaiting(false);
    void (async () => {
      try {
        const caps = await fetchBrokerCapabilities();
        if (cancelled) return;
        const ctraderCap = caps.find((c) => String(c.broker_name).toLowerCase() === "ctrader");
        setOpenApiSync(resolveCtraderOpenApiSync(ctraderCap ?? null));
      } catch (error) {
        if (!cancelled) presentMutationError(error, "Could not load cTrader connect settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.open]);

  React.useEffect(() => {
    if (!props.open) {
      clearPopupWatch();
      clearCtraderOAuthPending();
    }
  }, [clearPopupWatch, props.open]);

  React.useEffect(() => {
    props.onBusyChange(loading || oauthStarting || oauthWaiting || submitting);
  }, [loading, oauthStarting, oauthWaiting, props, submitting]);

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isCtraderOAuthPopupMessage(event.data)) return;

      clearPopupWatch();
      setOauthStarting(false);

      if (event.data.status === "success") {
        void finishAfterOAuth();
        return;
      }

      setOauthWaiting(false);
      if (event.data.status === "error") {
        toastError("cTrader sign-in failed", {
          description: event.data.message || "Unable to complete cTrader OAuth.",
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      clearPopupWatch();
    };
  }, [clearPopupWatch, finishAfterOAuth]);

  const handleOAuthConnect = async () => {
    if (!consentGranted) {
      toastError("Check the consent box to continue.");
      return;
    }
    if (!openApiSync?.oauthAuthorizePath) {
      toastError("cTrader OAuth is not available in this environment.");
      return;
    }

    const assetClass = selection.assetClass.trim().toLowerCase();
    stashCtraderOAuthAssetClasses(assetClass ? [assetClass] : []);
    stashCtraderOAuthEnvironment(environment);
    stashCtraderOAuthMdFetchConsent(true);

    setOauthStarting(true);
    try {
      const { authorize_url } = await fetchCtraderOAuthAuthorizeUrl(
        openApiSync.oauthAuthorizePath,
        environment
      );
      clearPopupWatch();
      const popup = openCtraderOAuthPopup(authorize_url);
      if (!popup) {
        toastError("Popup blocked", {
          description: "Allow popups for this site to sign in with cTrader.",
        });
        return;
      }
      setOauthWaiting(true);
      popupPollRef.current = window.setInterval(() => {
        if (popup.closed) {
          clearPopupWatch();
          setOauthStarting(false);
          setOauthWaiting(false);
        }
      }, 400);
    } catch (error) {
      presentMutationError(error, "Could not start cTrader sign-in");
    } finally {
      setOauthStarting(false);
    }
  };

  const busy = loading || oauthStarting || oauthWaiting || submitting;

  return (
    <>
      <div className="flex flex-col gap-4">
        <ol className={cn(BROKER_FIELD_DESC_CLASS, "m-0 list-decimal space-y-1 pl-5 text-sm")}>
          <li>Sign in with cTrader (OAuth popup)</li>
          <li>Confirm OHLC for {symbol} on this account</li>
        </ol>

        {loading ? (
          <div className={cn(BROKER_FIELD_DESC_CLASS, "flex items-center gap-2")}>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Loading cTrader connect…
          </div>
        ) : (
          <>
            <Field className="gap-2">
              <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Environment</FieldLabel>
              <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                Choose demo or live — must match the cTrader account you use for OHLC.
              </FieldDescription>
              <RadioGroup
                value={environment}
                onValueChange={(value) => setEnvironment(value as "demo" | "live")}
                className="flex flex-row flex-wrap gap-6"
                disabled={busy}
              >
                {(["demo", "live"] as const).map((env) => {
                  const inputId = `md-reconnect-ctrader-env-${env}`;
                  return (
                    <div key={env} className="flex min-w-0 items-start gap-2">
                      <RadioGroupItem value={env} id={inputId} className="mt-0.5" />
                      <Label htmlFor={inputId} className={cn(BROKER_FIELD_DESC_CLASS, "font-normal")}>
                        {env === "demo" ? "Demo" : "Live"}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </Field>

            <ConsentField
              id="md-reconnect-consent-ctrader"
              symbol={symbol}
              source={props.source}
              brokerSlug="ctrader"
              checked={consentGranted}
              disabled={busy}
              onCheckedChange={setConsentGranted}
            />

            {oauthWaiting ? (
              <p className={cn(BROKER_FIELD_DESC_CLASS, "m-0 text-sm")}>
                Complete sign-in in the cTrader window. This dialog saves your OHLC preference
                when OAuth finishes — no trade sync.
              </p>
            ) : null}
          </>
        )}
      </div>

      <div className="mt-4 flex w-full flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
        <Button
          type="button"
          variant="outline-gray"
          size="sm"
          data-font="primary"
          className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
          disabled={busy}
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
          disabled={busy || !consentGranted || !openApiSync}
          onClick={() => void handleOAuthConnect()}
        >
          {oauthStarting || oauthWaiting || submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              {submitting ? "Saving…" : oauthWaiting ? "Waiting for sign-in…" : "Opening…"}
            </>
          ) : (
            `Connect & confirm ${symbol}`
          )}
        </Button>
      </div>
    </>
  );
}

export function MarketDataBrokerReconnectDialog({
  open,
  onOpenChange,
  broker,
  symbol,
  brokerAccountCode,
  row,
  source,
  selection,
  resolveContext,
  onFlowComplete,
}: MarketDataBrokerReconnectDialogProps): React.ReactElement {
  const brokerSlug = broker.trim().toLowerCase();
  const display = brokerLabel(brokerSlug, source);
  const isLiquid = isInlineLiquidMarketDataReconnect(brokerSlug);
  const isCtrader = isInlineCtraderMarketDataReconnect(brokerSlug);
  const [busy, setBusy] = React.useState(false);

  const sharedProps: SharedFlowProps = {
    open,
    symbol,
    display,
    brokerAccountCode,
    row,
    source,
    brokerSlug,
    selection,
    resolveContext,
    onFlowComplete,
    onOpenChange,
    onBusyChange: setBusy,
  };

  return (
    <StrategyPineDetailDialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
      title={`Connect ${display} for ${symbol}`}
      description="Reconnect your broker and confirm this OHLC source in one step — no trade sync."
      size="compact"
      contentClassName="sm:max-w-lg"
      showDismissButton={false}
      contentProps={{
        "data-market-data-broker-reconnect-dialog": "",
        stackLayer: "top",
      }}
    >
      {isLiquid ? <LiquidMarketDataReconnectForm {...sharedProps} /> : null}
      {isCtrader ? <CtraderMarketDataReconnectForm {...sharedProps} /> : null}
    </StrategyPineDetailDialog>
  );
}
